import {AgGridReact} from "ag-grid-react";
import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {ColDef, GetRowIdParams, GridOptions, IServerSideGetRowsParams} from "ag-grid-community";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {STATE_SUMMARY_UPDATING} from "../../../constants/FinanceRecoil";
import {FilterDef} from "../../../constants/FinanceModels";
import ColouredChip from "../../../component/ColouredChip";
import {formatDate} from "../../../utils/FormatUtils";
import {getLocalGridState, setLocalGridState} from "../../../utils/GridUtils";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import {NumericSummary} from "../../../component/NumericSummary";
import EmptyState from "../../../../../util/component/EmptyState";
import {useAgGridLocale} from "../../../../../config/globalConfig";
import {
    COLOR_BILLABLE,
    COLOR_BILLED_PAID,
    COLOR_BILLED_UNPAID,
    COLOR_REJECTED,
    ID_BILLABLE,
    ID_BILLED_PAID,
    ID_BILLED_UNPAID,
    ID_REJECTED,
    STATE_ETOLL_CHARGES_ACTIVE_FILTERS,
    STATE_ETOLL_CHARGES_TOTAL_COUNT,
    STATE_ETOLL_CHARGES_UPDATING
} from "../constants/ETollConstants";
import {useETollChargesRepository} from "../../../api/repositories/ETollChargesRepository";
import ETollMerfDetails from "./ETollMerfDetails";
import {IconButton, Tooltip} from "@mui/material";
import {InfoRounded} from "@mui/icons-material";
import Chip from "@mui/material/Chip";

const ETollChargesTable = () => {

    const UNIQUE_ID = "fnn-charges";

    const {t} = useTranslation('finance', {keyPrefix: 'etoll'});
    const {getCharges, getSummary} = useETollChargesRepository();

    const setTotalCount = useSetRecoilState(STATE_ETOLL_CHARGES_TOTAL_COUNT);
    const chargeFilterDefs = useRecoilValue(STATE_ETOLL_CHARGES_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_ETOLL_CHARGES_UPDATING);
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const agGridLocale = useAgGridLocale();
    const gridRef = useRef<AgGridReact>(null);
    const [tollDetails, setTollDetails] = useState<any>();
    const [openTollDetails, setOpenTollDetails] = useState<any>();

    const columnDefs: ColDef[] = [
        {
            field: 'report.fileName',
            headerName: t('tables.charge.reportName'),
            headerTooltip: t('tables.charge.reportName'),
            suppressSizeToFit: true,
            minWidth: 250,
            unSortIcon: true,
            sortable: true,
            pinned: "left",
            lockPosition: "left",
            lockVisible: true,
            lockPinned: true
        },
        {
            field: 'equipmentNumber',
            headerName: t('tables.charge.equipmentNumber'),
            headerTooltip: t('tables.charge.equipmentNumber'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'registration',
            headerName: t('tables.charge.plate'),
            headerTooltip: t('tables.charge.plate'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'entryDate',
            headerName: t('tables.charge.entryDate'),
            headerTooltip: t('tables.charge.entryDate'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatDate(params.value)
        },
        {
            field: 'exitTollName',
            headerName: t('tables.charge.exitTollName'),
            headerTooltip: t('tables.charge.exitTollName'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'vatRate',
            headerName: t('tables.charge.vatRate'),
            headerTooltip: t('tables.charge.vatRate'),
            minWidth: 150,
            unSortIcon: true,
            cellRenderer: (params: any) =>  params.value ? params.value === 1 ? "23%" : params.value === 2 ? "6%" : "" : ""
        },
        {
            field: 'fareAmount',
            headerName: t('tables.charge.fareAmount'),
            headerTooltip: t('tables.charge.fareAmount'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'customerId',
            headerName: t('tables.charge.customerId'),
            headerTooltip: t('tables.charge.customerId'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'raCode',
            headerName: t('tables.charge.raCode'),
            headerTooltip: t('tables.charge.raCode'),
            minWidth: 200, unSortIcon: true
        },
        {
            field: 'status', headerName: t('tables.charge.status'), headerTooltip: t('tables.charge.status'),
            minWidth: 250, unSortIcon: true,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                const status = params.data.status;
                switch (status) {
                    case ID_REJECTED:
                        return {
                            component: () =>
                                <Tooltip title={t('tables.charge.tollErrorDetails')}>
                                    <>
                                        <Chip label={t(`statuses.REJECTED`)} color={COLOR_REJECTED}/>
                                        <span>
                                            <IconButton color="accent" onClick={() => {
                                                {
                                                    setTollDetails(params.data);
                                                    setOpenTollDetails(true);
                                                }
                                            }}>
                                                <InfoRounded/>
                                            </IconButton>
                                        </span>
                                    </>
                                </Tooltip>
                        };
                    case ID_BILLABLE:
                        return {
                            component: () => <ColouredChip name={ID_BILLABLE} color={COLOR_BILLABLE}/>
                        };
                    case ID_BILLED_PAID:
                        return {
                            component: () => <ColouredChip name={ID_BILLED_PAID} color={COLOR_BILLED_PAID}/>
                        }
                    case ID_BILLED_UNPAID:
                        return {
                            component: () =>
                                <Tooltip title={t('tables.charge.tollErrorDetails')}>
                                    <>
                                        <Chip label={t(`statuses.BILLED_UNPAID`)} color={COLOR_BILLED_UNPAID}/>
                                        <span>
                                            <IconButton color="accent" onClick={() => {
                                                {
                                                    setTollDetails(params.data);
                                                    setOpenTollDetails(true);
                                                }
                                            }}>
                                                <InfoRounded/>
                                            </IconButton>
                                        </span>
                                    </>
                                </Tooltip>
                        }
                    default:
                        break;
                }
            }
        },
        {
            field: "dealer", headerName: t("tables.report.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
        },
        {
            field: "legalEntity", headerName: t("tables.report.legalEntity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
        }
    ];


    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {title: t('tables.charge.no-rows-overlay.title')}
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(chargeFilterDefs))
        setSummaryUpdating(true);
    }, [chargeFilterDefs])


    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(chargeFilterDefs));
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating])

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[]) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                getCharges(startRow, endRow - startRow, sort, order, filterDefs)
                    .then(response => {
                        let result = response.result;

                        setTotalCount(result["total-amount"]);

                        if (result["total-amount"] === 0) {
                            gridRef.current!.api.showNoRowsOverlay();
                        } else {
                            gridRef.current!.api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result["total-amount"]
                        });
                    })
                    .catch(() => params.fail());
            }
        };
    }


    const toolbar = {
        renderInfo: () => (
            <NumericSummary query={() => getSummary(chargeFilterDefs)}
                            fields={[ID_REJECTED, ID_BILLABLE, ID_BILLED_PAID, ID_BILLED_UNPAID]}
                            i18nPath={"etoll.counters.charges"}/>
        )
    }


    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api?.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    return (
        <>
            <AgGridCustomTable
                toolbar={toolbar}
                rowModelType='serverSide'
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                noRowsOverlayComponent={noRowsOverlayComponent}
                suppressRowClickSelection
                onGridReady={() => {
                    let gridState = getLocalGridState(UNIQUE_ID).cols;
                    if (gridState !== undefined) {
                        gridRef.current!.columnApi.applyColumnState({
                            state: gridState,
                            applyOrder: true
                        })
                    }

                    gridRef.current!.api.sizeColumnsToFit();
                    gridRef.current!.api.setServerSideDatasource(createDatasource(chargeFilterDefs));
                }}
                onColumnMoved={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onPaginationChanged={() => {
                    setLocalGridState(UNIQUE_ID, {
                        rows: gridRef.current!.api?.paginationGetPageSize(),
                        cols: getLocalGridState(UNIQUE_ID).cols
                    });
                }}
            />
            <ETollMerfDetails tollDetails={tollDetails} open={openTollDetails}
                              onClose={() => {
                                  setOpenTollDetails(false);
                              }}/>
        </>
    );
}

export default ETollChargesTable;
